<template>
  <mf-loading-dialog :loading="loading">
    <base-page-layout id="industry_proposal_page_access" :title="getPageTitle">
      <div class="view-offer-trade-container">
        <mf-button
          v-if="isMobile"
          id="industry_proposal_back_button"
          label="Voltar"
          color="marca-0-l40"
          icon="arrow_back"
          icon-position="left"
          text
          @click="goBack"
        />
        <div ref="sentinel" class="sentinel"></div>
        <div ref="stickyHeader" :class="['sticky-buttons', { 'is-sticky': isSticky }]">
          <mf-button
            v-if="!isMobile"
            id="industry_proposal_back_button"
            label="Voltar"
            color="marca-0-l40"
            icon="arrow_back"
            icon-position="left"
            text
            @click="goBack"
          />
          <div v-if="includesThisStatus(['industry_proposal'])" class="base-row-flex">
            <reject-offer-dialog :offer-schedule-id="$route.params.id" :is-processing="includesThisStatus(['processing'])" />
            <accept-offer-dialog ref="acceptOfferDialog" :campanha="offerSchedule" />
          </div>
          <mf-action-buttons
            v-else
            :primary-button="{
              isVisible: true,
              isDisabled: includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']),
              isLoading: this.takingAction,
              action: this.save,
              text: this.saveButtonText,
            }"
            :secondary-button="secondaryButton"
          />
        </div>

        <header-offer-trade :offer-schedule="offerSchedule" />
        <communication-offer-trade :offer-schedule="offerSchedule" />
        <offer-products-list :offer-schedule="offerSchedule" />
      </div>
      <offer-schedule-error-dialog v-model="showErrorDialog" :is-new-offer="false" />
      <processing-dialog v-model="showProcessingDialog" :message="processingMessage" @go-back="goBack" />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import { DateTime } from 'luxon'
import BasePageLayout from '@/components/BasePageLayout.vue'
import RejectOfferDialog from '../components/offers-management/RejectOfferDialog.vue'
import AcceptOfferDialog from '../components/offers-management/AcceptOfferDialog.vue'
import { QUERY_SALES_CAMPAIGN_BY_ID, MUTATION_FINISH_SALES_CAMPAIGN, MUTATION_UNSCHEDULED_SALES_CAMPAIGN } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { campaignHelpers } from '@/modules/campanhas/gestao-ofertas-v2/views/mixins/campaign-helpers'
import { industryWarnings } from '@/modules/campanhas/gestao-ofertas-v2/views/mixins/industry-warnings'
import { tradeCampaignProcessing } from '@/modules/industria/ofertas-trade/views/mixins/trade-campaign-processing'
import HeaderOfferTrade from '../components/offers-management/HeaderOfferTrade.vue'
import CommunicationOfferTrade from '../components/offers-management/CommunicationOfferTrade.vue'
import OfferProductsList from '../components/offers-management/OfferProductsList.vue'
import Swal from 'sweetalert2'
import OfferScheduleErrorDialog from '@/modules/campanhas/gestao-ofertas-v2/views/components/OfferScheduleErrorDialog.vue'
import ProcessingDialog from '../components/offers-management/ProcessingDialog.vue'

export default {
  name: 'ViewOfferTrade',
  components: {
    BasePageLayout,
    RejectOfferDialog,
    AcceptOfferDialog,
    HeaderOfferTrade,
    CommunicationOfferTrade,
    OfferProductsList,
    OfferScheduleErrorDialog,
    ProcessingDialog,
  },
  mixins: [campaignHelpers, industryWarnings, tradeCampaignProcessing],
  data: () => ({
    loading: true,
    takingAction: false,
    isStaticSegment: false,
    showErrorDialog: false,
    isSticky: false,
    observer: null,
    offerSchedule: {
      types: [],
    },
    campanha: {},
    showProcessingDialog: false,
  }),
  computed: {
    getPageTitle() {
      return this.offerSchedule.status === 'industry_proposal' ? 'Visualizar proposta' : 'Visualizar campanha'
    },
    secondaryButton() {
      const button = {
        isVisible: this.includesThisStatus(['active', 'scheduled']),
        isDisabled: this.includesThisStatus(['finished', 'expired', 'industry_rejected', 'processing']),
        isLoading: this.takingAction,
      }

      switch (this.offerSchedule.status) {
        case 'active':
          button.text = 'Finalizar'
          button.action = this.finishCampaign
          break
        case 'scheduled':
        default:
          button.text = 'Suspender'
          button.action = this.disableCampaign
          break
      }
      return button
    },
    proposalDeadlineExpired() {
      return DateTime.now() > DateTime.fromISO(this.offerSchedule?.industry?.proposal_deadline).endOf('day')
    },
    processingMessage() {
      return this.timerPartialValue === this.timerTotalValue ? 'Estamos processando suas alterações na oferta...' : this.timerLabel
    },
    hasExternalSynchronization() {
      return Boolean(this.offerSchedule?.external_synchronization)
    },
    saveButtonText() {
      return this.hasExternalSynchronization ? 'Salvar e reprocessar' : 'Salvar alterações'
    },
  },
  async mounted() {
    await this.getData()
    await this.subscribeToOfferSchedule()
    this.$nextTick(() => {
      this.setupStickyObserver()
    })
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    goBack() {
      if (this.includesThisStatus(['industry_proposal'])) {
        this.$router.push({ name: 'ofertas-trade-propostas' })
      } else if (this.includesThisStatus(['active', 'scheduled', 'processing'])) {
        this.$router.push({ name: 'ofertas-trade-aceitas' })
      } else if (this.includesThisStatus(['industry_rejected', 'expired', 'finished'])) {
        this.$router.push({ name: 'ofertas-trade-historico' })
      }
    },

    setupStickyObserver() {
      const options = {
        rootMargin: '-64px 0px 0px 0px',
        threshold: [0, 1],
      }

      this.observer = new IntersectionObserver(([entry]) => {
        this.isSticky = !entry.isIntersecting
      }, options)

      this.$nextTick(() => {
        if (this.$refs.sentinel) {
          this.observer.observe(this.$refs.sentinel)
        }
      })
    },

    async getData() {
      this.loading = true
      try {
        const response = await this.$apollo.query({
          query: QUERY_SALES_CAMPAIGN_BY_ID,
          variables: {
            id: this.$route.params.id,
          },
          fetchPolicy: 'no-cache',
        })

        const { offerSchedule } = response.data
        this.offerSchedule = {
          ...this.formatCampaign(offerSchedule),
        }

        if (this.offerSchedule?.segments?.length > 0) {
          if (this.offerSchedule?.segments[0]?.fixed) {
            this.isStaticSegment = true
          }
        }
      } catch (error) {
        console.error(error)
        this.$snackbar({ message: 'Ocorreu um erro ao buscar a oferta', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },

    async save() {
      this.takingAction = true
      const isDraft = false
      this.setCompatibilityModeWithMixin()
      const campaignToSave = this.formatCampaignToSave(isDraft)

      await this.processCampaign(campaignToSave, 'Erro ao salvar alterações')
    },

    async finishCampaign() {
      let confirmed = await this.$confirm({
        ...this.industryFinishWarning('concluída'),
      })
        .then(() => true)
        .catch(() => false)
      if (!confirmed) return

      this.takingAction = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_FINISH_SALES_CAMPAIGN,
          variables: {
            id: this.offerSchedule._id,
          },
        })
        this.goBack()
      } catch (err) {
        console.log(err)
        Swal.fire({
          text: 'Erro ao finalizar a campanha',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
      } finally {
        this.takingAction = false
      }
    },

    async disableCampaign() {
      let confirmed = false

      if (this.proposalDeadlineExpired) {
        confirmed = await this.$confirm({
          title: 'Suspender campanha',
          message: `Ao suspender essa campanha não será possível ativá-la novamente. Essa campanha será expirada, pois o período de adesão foi encerrado.`,
          confirmText: 'Suspender',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      } else {
        confirmed = await this.$confirm({
          title: 'Suspender campanha',
          message: `A campanha voltará ao status proposta da indústria.`,
          confirmText: 'Suspender',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
      }

      if (!confirmed) return

      this.takingAction = true
      try {
        await this.$apollo.mutate({
          mutation: MUTATION_UNSCHEDULED_SALES_CAMPAIGN,
          variables: {
            id: this.offerSchedule._id,
          },
        })
        this.goBack()
      } catch (err) {
        console.log(err)
        Swal.fire({
          text: 'Erro ao suspender a campanha',
          icon: 'error',
          showConfirmButton: false,
          timer: 1000,
          customClass: {
            content: 'content-class',
          },
        })
      } finally {
        this.takingAction = false
      }
    },

    includesThisStatus(status) {
      return status.includes(this.offerSchedule.status)
    },

    setCompatibilityModeWithMixin() {
      this.campanha = { ...this.offerSchedule }
    },

    onSubscriptionStart() {
      this.takingAction = true
      this.showProcessingDialog = true
    },

    onSubscriptionSuccess() {
      this.showProcessingDialog = false
      this.takingAction = false
      this.showSuccessMessage('Alterações salvas com sucesso')
    },

    onSubscriptionError() {
      this.showProcessingDialog = false
      this.takingAction = false
      this.showErrorMessage('Erro ao salvar alterações')
    },

    onClose() {
      this.goBack()
    },
  },
}
</script>

<style lang="scss" scoped>
.sentinel {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  visibility: hidden;
}

.sticky-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 64px;
  z-index: 1;
  background-color: #f6f6f8;
  padding: 14px 20px;
  margin-left: -12px;
  margin-right: -12px;
  transition: all 0.3s ease;

  &.is-sticky {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    background-color: #ffffff;
    z-index: 3;
  }

  @media screen and (max-width: $xs-breakpoint) {
    top: 56px;
    justify-content: center;
  }
}

.base-row-flex {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: $xs-breakpoint) {
    flex: 1;
    justify-content: space-between;
  }
}

.view-offer-trade-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
}
</style>
